import { useAuthContext } from "contexts/AuthContext";
import FoodUpdater from "data/FoodUpdater";
import moment from "moment";
import { useEffect, useState } from "react";
import type { DropdownItemProps, DropdownProps } from "semantic-ui-react";
import { Button, Card, Dropdown, Form, Input, Loader, Message } from "semantic-ui-react";
import type { FoodOrder, FoodUpdaterResponse, OrderPayment } from "types";

import * as prohealth from "./prohealth-api"


/**
 * What the make-payment form accepts.
 *
 * Established from its only call site, `./index.tsx:46`, which forwards each
 * field straight off `Solutran`'s own props; those in turn come from
 * `food/index.tsx:489`, where `ordersData`/`ordersMutate` are the `useSWR`
 * response for `/order?patientId=…` and the two `solutran*` values are that
 * component's `useState<string>()` pair.
 *
 * `ordersData` is optional because SWR 0.5.7 declares `SWRResponse.data` as
 * `data?: Data`, so it is genuinely absent on the first render — which is what
 * the `props.ordersData?.forEach` at line 52 already reflects.
 *
 * `setView` and `setSolutranMakePaymentSelectedOrder` are typed as accepting
 * `null` rather than as React's `Dispatch<SetStateAction<string | undefined>>`:
 * this file calls both with a literal `null` (lines 86-87, 124-125), while the
 * parent's state is `useState<string>()`. That disagreement is pre-existing and
 * is described at the SUSPECTED DEFECT note below.
 */
type MakePaymentProps = {
    // The two optionals carry an explicit `| undefined`: both are threaded down
    // as JSX attributes from values that are themselves absent before the first
    // fetch / first selection, which is a pass of `undefined`, not an omission.
    ordersData?: FoodOrder[] | undefined;
    /** SWR's revalidate; only awaited for its completion, never for its value. */
    ordersMutate: () => Promise<unknown>;
    /**
     * Payer org, used unguarded as an index key into `VITE_PROHEALTH_PRODUCTS`.
     *
     * `string | null | undefined` because that is what `food/index.tsx` holds —
     * `home/index.tsx` passes the literal `null`. In practice this tab renders
     * only when `patientId` is non-null, whose caller passes a required
     * `flatpatient.org`, but nothing in the types enforces that pairing, so the
     * index read below is asserted.
     */
    org: string | null | undefined;
    /**
     * The parent's `useState` dispatchers. Both are only ever called from this
     * file with a literal `null`, which is all the parameter names. The parent's
     * own state types include `null` to match.
     */
    setView: (view: null) => void;
    /** The order id chosen on the Orders tab, or absent when none is selected. */
    solutranMakePaymentSelectedOrder?: string | null | undefined;
    setSolutranMakePaymentSelectedOrder: (orderId: null) => void;
};

/**
 * The per-org bundle map parsed out of `VITE_PROHEALTH_PRODUCTS`.
 *
 * Only the key sets are read — the org key selects a bundle map, and that map's
 * own keys become the dropdown options. Nothing in this app constrains the
 * values or the key spaces, so both records are open and the value is `unknown`.
 *
 * `noUncheckedIndexedAccess` makes each read `… | undefined`, and the truthiness
 * check does not narrow the second read: the key is `props.org`, a property
 * access rather than a `const`, so TypeScript will not treat the two index
 * expressions as the same reference. Hence the assertion on the `Object.keys`
 * argument below.
 */
type ProHealthProducts = Record<string, Record<string, unknown>>;

export default function MakePayment(props: MakePaymentProps) {

    const authInfo = useAuthContext();

    // Each of these is cleared with an explicit `setX(null)` rather than
    // `undefined`, so `null` is part of the state type as written.
    const [loader, setLoader] = useState<string | null>()
    /**
     * The processor's balance and reason, shown on the success card. Both fields
     * are written straight off the reply, which leaves them optional, so both
     * carry an explicit `| undefined` for `exactOptionalPropertyTypes`.
     */
    const [paymentSuccess, setPaymentSuccess] = useState<{ balance?: string | undefined; status?: string | undefined } | null>()
    const [orderSuccess, setOrderSuccess] = useState<boolean | null>()
    const [error, setError] = useState<string | null>()
    const [cardNumber, setCardNumber] = useState<string>()
    const [bundle, setBundle] = useState<string>()
    const [paymentType, setPaymentType] = useState<string>()
    /**
     * Options built from payment ids, which are optional — so an option's three
     * fields can each hold `undefined`. `DropdownItemProps` forbids that under
     * `exactOptionalPropertyTypes`; see the same case in `pastPayments.tsx`.
     */
    const [existingPaymentOptions, setExistingPaymentOptions] = useState<{ key: string | undefined; text: string | undefined; value: string | undefined }[]>([])
    const [existingPaymentOption, setExistingPaymentOption] = useState<string>()

    /*
     * SUSPECTED DEFECT (this line) — REPORTED, NOT FIXED.
     * `VITE_PROHEALTH_PRODUCTS` is declared optional (`vite-env.d.ts:55`), and
     * `JSON.parse(undefined)` throws a SyntaxError rather than yielding a falsy
     * value. In a deployment that has not set the variable, this throws during
     * render and takes the whole Make Payment tab down. Asserted non-null rather
     * than defaulted: supplying `?? '{}'` here would turn a loud misconfiguration
     * into a silently empty bundle dropdown.
     */
    const products = JSON.parse(import.meta.env.VITE_PROHEALTH_PRODUCTS!) as ProHealthProducts

    const bundleOptions: DropdownItemProps[] = products[props.org!]
        ? Object.keys(products[props.org!]!).map(k => (
            { key: k, text: k, value: k }
        ))
        : []

    const paymentTypes = [
        { key: "n", text: "Make New Payment", value: "new" },
        { key: "o", text: "Add to Existing Payment", value: "old" }
    ]

    useEffect(() => {
        setLoader("Fetching orders data...")
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection leaves the loader spinning with no error shown
        props.ordersMutate().then(() => {
            setLoader(null)
            /*
             * `(string | undefined)[]`, not `string[]`: {@link OrderPayment.paymentId}
             * is optional because `makePayment` copies it straight off a reply that
             * leaves it optional, and nothing filters those out before they land
             * here. A payment written from such a reply therefore contributes an
             * `undefined` entry, which line 132 turns into a dropdown option whose
             * key, text and value are all `undefined` — it renders as a blank row
             * that selects nothing. SUSPECTED DEFECT, reported not fixed: stating
             * the union keeps the empty option visible rather than filtering it out,
             * which would change what the dropdown shows.
             */
            const existingPayments: (string | undefined)[] = []
            props.ordersData?.forEach(o => {
                o.payments?.forEach(p => {
                    if (p.paymentStatus === "paid") {
                        if (!existingPayments.includes(p.paymentId)) {
                            existingPayments.push(p.paymentId)
                        }
                    }
                    else {
                        const i = existingPayments.indexOf(p.paymentId)
                        if (i >= 0) {
                            existingPayments.splice(i, 1)
                        }
                    }
                })
            })

            const paymentsOptions = existingPayments.map(a => ({ key: a, text: a, value: a }))
            setExistingPaymentOptions(paymentsOptions)
        })
    }, [])

    // `DropdownProps['value']` is `boolean | number | string | (…)[]`. Every
    // dropdown here is single-select over string-valued options built in this
    // file, so `string` is what arrives. Asserted rather than guarded — a runtime
    // check would be new behaviour on a path that assigns straight through.
    const handleBundleDropdown = (e: React.SyntheticEvent<HTMLElement>, value: DropdownProps) => {
        setBundle(value.value as string)
    }

    const handlePaymentTypeDropdown = (e: React.SyntheticEvent<HTMLElement>, value: DropdownProps) => {
        setPaymentType(value.value as string)
    }

    const handlePaymentOptionDropdown = (e: React.SyntheticEvent<HTMLElement>, value: DropdownProps) => {
        setExistingPaymentOption(value.value as string)
    }

    const handleDismiss = () => {
        props.setView(null)
        props.setSolutranMakePaymentSelectedOrder(null)
    }

    const makePayment = async (cardNumber: string, bundle: string) => {
        setPaymentSuccess(null)
        setOrderSuccess(null)
        setError(null)
        setLoader("Authorizing payment...")
        await prohealth.authorizePayment(authInfo.token, cardNumber, props.org!, bundle)
            .then(
                async (response) => {
                    if (response.data.status === 0) {
                        // `data` is non-null asserted throughout this branch: a
                        // `status: 0` reply that omitted it already threw here
                        // before this annotation. See the SUSPECTED DEFECT note
                        // on `ProHealthEnvelope` in ./prohealth-api.ts.
                        if (response.data.data!.statusCode === 1) {
                            setPaymentSuccess({ balance: response.data.data!.balance, status: response.data.data!.statusReason })
                            setLoader("Updating order info on CareBetty...")

                            /*
                             * SUSPECTED DEFECT (line 161) — REPORTED, NOT FIXED.
                             * `props.ordersData` is `FoodOrder[] | undefined` (SWR 0.5.7 declares
                             * `data?: Data`) and `.find` yields `FoodOrder | undefined`, yet lines
                             * 173-177 read and write `order` unguarded. Both misses are reachable:
                             * `ordersData` is absent while the initial fetch is in flight, and the
                             * `find` misses whenever the selected order id is not in the current
                             * page of orders. Either throws a TypeError *after* the card has
                             * already been charged at line 153 — the money moves and the order
                             * never records it. Non-null asserted rather than guarded because a
                             * guard here would swallow the failed write silently, which on a
                             * payment path is worse than the crash.
                             */
                            const order = props.ordersData!.find(o => o.orderId === props.solutranMakePaymentSelectedOrder)!

                            const newPayment: OrderPayment = {
                                paymentStatus: "paid",
                                paymentDate: moment().utc().format(),
                                paymentId: response.data.data!.orderId,
                                paymentAuthCode: response.data.data!.authorizeCode,
                                paymentType: "solutran",
                                paymentAmount: response.data.data!.authorizeAmount,
                                paymentProductName: bundle,
                            }

                            order["lastUpdated"] = moment().utc().format();
                            order["lastUpdatedBy"] = authInfo.practitionerName;
                            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the `if` that satisfies the rule is a different expression
                            order["payments"] ? order["payments"].push(newPayment) : order["payments"] = [newPayment]

                            await FoodUpdater(`/order/` + order.orderId, authInfo.token, order).then(
                                (response: FoodUpdaterResponse) => {
                                    setLoader(null);
                                    if (response.statusCode === "200") {
                                        setOrderSuccess(true)
                                        props.setView(null)
                                        props.setSolutranMakePaymentSelectedOrder(null)
                                        setError(null)
                                    } else {
                                        setError("ERROR with updating order - " + response.message);
                                    }
                                }, (error: Error) => {
                                    setLoader(null)
                                    // Concatenation, not arithmetic: the operands coerce to string
                                    // as upstream intends. Wrapping them in String() would emit a
                                    // call.
                                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                                    setError("ERROR with updating order - " + error);
                                }
                            )
                        } else {
                            setError("ERROR: " + response.data.data!.statusReason)
                            setLoader(null)
                        }
                    } else {
                        setLoader(null);
                        setError(response.data.message ?? "ERROR: could not authorizing payment.")
                    }
                },
                (error: Error) => {
                    setLoader(null);
                    setError(error.message)
                });
    };

    const addToExistingPayment = async () => {
        setPaymentSuccess(null)
        setOrderSuccess(null)
        setError(null)
        setLoader("Updating order info on CareBetty...")

        // Same unguarded `find` as in `makePayment` — see the note at line 161.
        const order = props.ordersData!.find(o => o.orderId === props.solutranMakePaymentSelectedOrder)!

        /*
         * SUSPECTED DEFECT (lines below) — REPORTED, NOT FIXED. Three distinct
         * failures live in this loop, all of which the `any` was hiding:
         *
         * 1. `existingPayment` is seeded with `{}` and only overwritten on a
         *    match. When `existingPaymentOption` matches no payment on any order,
         *    that bare `{}` is pushed onto `order.payments` and PUT to the API —
         *    a payment record with no id, status, date or amount. The past-payments
         *    table then folds it in as a row keyed `undefined`. Typed as
         *    `OrderPayment | Record<string, never>` to state that the empty arm is
         *    genuinely reachable rather than to make it safe.
         *
         * 2. `props.ordersData[i].payments?.length ?? 0` is the loop bound: an order
         *    with no `payments` yields `0`, matching the `j < undefined` the raw
         *    optional chain produced, so the inner loop is skipped either way. The
         *    next line indexes `payments[j]` WITHOUT the `?.`, which is only
         *    unreachable because of that skip; it is left as written, and the
         *    assertion there records the dependency on the bound.
         *
         * 3. The matched payment is pushed by reference, not copied, so the same
         *    object now sits on two orders. A later in-place edit of one order's
         *    payment silently mutates the other's.
         */
        let existingPayment: OrderPayment | Record<string, never> = {}
        for (let i = 0; i < props.ordersData!.length; i++) {
            let b = false
            for (let j = 0; j < (props.ordersData![i]!.payments?.length ?? 0); j++) {
                if (props.ordersData![i]!.payments![j]!.paymentId === existingPaymentOption) {
                    existingPayment = props.ordersData![i]!.payments![j]!
                    b = true
                    break
                }
            }
            if (b) { break }
        }

        order["lastUpdated"] = moment().utc().format();
        order["lastUpdatedBy"] = authInfo.practitionerName;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the `if` that satisfies the rule is a different expression
        order["payments"] ? order["payments"].push(existingPayment as OrderPayment) : order["payments"] = [existingPayment as OrderPayment]

        await FoodUpdater(`/order/` + order.orderId, authInfo.token, order).then(
            (response: FoodUpdaterResponse) => {
                setLoader(null);
                if (response.statusCode === "200") {
                    setOrderSuccess(true)
                    props.setView(null)
                    props.setSolutranMakePaymentSelectedOrder(null)
                    setError(null)
                } else {
                    setError("ERROR with updating order - " + response.message);
                }
            }, (error: Error) => {
                setLoader(null)
                // Concatenation, not arithmetic: the operands coerce to string as upstream intends.
                // Wrapping them in String() would emit a call.
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                setError("ERROR with updating order - " + error);
            }
        )
    }

    return (
        <>
            {props.solutranMakePaymentSelectedOrder &&
                <Card>
                    <Message info size="small" onDismiss={handleDismiss}>
                        <Message.Header>Current Selected Order Info</Message.Header>
                        <Message.List>
                            <Message.Item>{"Order ID: " + props.solutranMakePaymentSelectedOrder}</Message.Item>
                        </Message.List>
                    </Message>
                </Card>}
            {!props.solutranMakePaymentSelectedOrder &&
                <Card>
                    <Message warning size="small">
                        <Message.Header>No Order Selected!</Message.Header>
                        <Message.Content>
                            {"Please select an order on the Orders tab and click 'Pay' to make a payment!"}
                        </Message.Content>
                    </Message>
                </Card>}
            {/*
              * `value` is asserted, not coerced. Semantic reads an `undefined`
              * value as *uncontrolled* and `''` as controlled-empty, so `?? ''`
              * — or seeding the state with `''` — would flip this Dropdown to
              * controlled on the first render and change what it displays.
              */}
            <Dropdown
                disabled={!props.solutranMakePaymentSelectedOrder}
                style={{ "marginRight": "10px" }}
                placeholder="Select Payment Type"
                value={paymentType!}
                selection
                upward={false}
                options={paymentTypes}
                onChange={(e, value) => handlePaymentTypeDropdown(e, value)}
                compact
            />
            {paymentType === "new" && <Form>
                <Form.Field inline>
                    <Input
                        disabled={!props.solutranMakePaymentSelectedOrder}
                        style={{ "width": "250px" }}
                        focus
                        placeholder="Enter Solutran Card Number"
                        onChange={(e) => setCardNumber(e.target.value)}
                    />
                    <Dropdown
                        disabled={!props.solutranMakePaymentSelectedOrder}
                        style={{ "marginRight": "10px" }}
                        placeholder="Select Bundle"
                        selection
                        upward={false}
                        options={bundleOptions}
                        onChange={(e, value) => handleBundleDropdown(e, value)}
                        compact
                    />
                    {/*
                      * SUSPECTED DEFECT (the onClick below) — REPORTED, NOT FIXED.
                      * `disabled` gates only on an order being selected, not on
                      * `cardNumber` or `bundle` having been filled in. Both are
                      * `useState<string>()` and stay `undefined` until the user
                      * types a number and picks a bundle, so pressing Make Payment
                      * straight away calls `authorizePayment(token, undefined,
                      * org, undefined)`, which builds the URL
                      * `…/authorize-payment/undefined` and posts a body with
                      * `productName: undefined`. Whatever the gateway makes of
                      * that, the app asked it to charge a card it cannot name.
                      * Asserted rather than gated: adding the two missing
                      * conditions to `disabled` is the right fix but it is a
                      * behaviour change.
                      */}
                    <Button
                        disabled={!props.solutranMakePaymentSelectedOrder}
                        primary
                        icon='payment'
                        content='Make Payment'
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget on a payment action; a rejection is dropped after the card may already be charged
                        onClick={() => { makePayment(cardNumber!, bundle!) }}
                    />
                </Form.Field>
            </Form>}
            {paymentType === "old" && <>
                <Form>
                    <Form.Field inline>
                        {/*
                          * `options={existingPaymentOptions}` is a standing TS2322,
                          * left as written for the same reason as the `value=` note
                          * above: the option list is built from payment ids that are
                          * genuinely optional, so its entries really do hold
                          * `undefined`, and `DropdownItemProps` has no room for that
                          * under `exactOptionalPropertyTypes`. Asserting the list to
                          * Semantic's type would hide the blank-option defect noted
                          * at the `existingPayments` declaration.
                          */}
                        <Dropdown
                            disabled={!props.solutranMakePaymentSelectedOrder}
                            style={{ "marginRight": "10px" }}
                            placeholder="Select Existing Payment"
                            selection
                            upward={false}
                            options={existingPaymentOptions as DropdownItemProps[]}
                            onChange={(e, value) => handlePaymentOptionDropdown(e, value)}
                            compact
                        />
                        {existingPaymentOption && <>
                            <Button
                                disabled={!props.solutranMakePaymentSelectedOrder}
                                primary
                                icon='payment'
                                content='Confirm'
                                // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget on a payment action; a rejection is dropped after the card may already be charged
                                onClick={() => { addToExistingPayment() }}
                            />
                        </>}
                    </Form.Field>
                </Form>
            </>}

            {loader &&
                <Card>
                    <Loader active inline content={loader} />
                </Card>}
            {paymentSuccess &&
                <Card>
                    <Message success size="small" header={"Payment success!"} list={['New Balance: $' + paymentSuccess.balance, 'Status Reason: ' + paymentSuccess.status]} />
                </Card>}
            {orderSuccess &&
                <Card>
                    <Message success size="small" header={"Order updated successfully!"} />
                </Card>}
            {error &&
                <Card>
                    <Message error size="small" header={"Error!"} content={error} />
                </Card>}
        </>
    )
}